// Car detail + editor — fetches /admin/cars/:id, renders an editable form
// for every non-localised operational field (pricing, vendor, location,
// fleet, status, etc.), plus moderation actions (approve/reject/feature/
// archive) along the top. Localised description/SEO (en/fr/ar/es) is edited
// in the Translations section below (cars_locales); the canonical Car copy
// from the vehicle catalog is shown as inherited defaults.
//
// Photos are DriveCars-managed on the linked catalog Car (vehicle catalog →
// entry → Images) and shared by every listing of that Car — the Photos panel
// here links through instead of uploading listing-level media.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { env } from '@/lib/env';
import { DefRow, DetailLayout, DetailSection } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  TextField,
  TextAreaField,
  NumberField,
  SelectField,
  BooleanField,
  RelationshipPicker,
  MultiRelationshipPicker,
  LocationPicker,
  type RelationshipOption,
  type SelectOption,
} from '@/components/forms';
import {
  approveCar,
  archiveCar,
  featureCar,
  fetchAdminAttributeValues,
  fetchAdminCarDetail,
  fetchAdminLocations,
  fetchAdminVendors,
  fetchCatalogEntry,
  patchCar,
  rejectCar,
  retrieveCarCopy,
  unfeatureCar,
  type AdminCarBookingDefaultState,
  type AdminCarDetail,
  type AdminCarPricingUnit,
  type AdminCarStatus,
  type CarPatchInput,
} from '@/lib/admin-api';
import { ApiError } from '@/lib/api';

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

const STATUS_OPTIONS: SelectOption<AdminCarStatus>[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending review' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];
const UNIT_OPTIONS: SelectOption<AdminCarPricingUnit>[] = [
  { value: 'day', label: 'Per day' },
  { value: 'hour', label: 'Per hour' },
];
const CURRENCY_OPTIONS: SelectOption<string>[] = [
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'MAD', label: 'MAD' },
  { value: 'AED', label: 'AED' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'GBP', label: 'GBP (£)' },
];
function currencySymbol(code: string): string {
  if (code === 'EUR') return '€';
  if (code === 'USD') return '$';
  if (code === 'GBP') return '£';
  return `${code} `;
}
const BOOKING_STATE_OPTIONS: SelectOption<AdminCarBookingDefaultState | ''>[] = [
  { value: '', label: '— (no override)' },
  { value: 'available', label: 'Available' },
  { value: 'unavailable', label: 'Unavailable' },
];

// ─── Editor draft ────────────────────────────────────────────────────

interface CarDraft {
  slug: string;
  status: AdminCarStatus;
  isFeatured: boolean;
  number: number | null;
  vendorId: number;
  locationId: number | null;
  vendorOption: RelationshipOption | null;
  locationOption: RelationshipOption | null;
  address: string;
  mapLatitude: number | null;
  mapLongitude: number | null;
  mapZoom: number | null;
  price: number | null;
  salePrice: number | null;
  unit: AdminCarPricingUnit;
  currency: string;
  securityDeposit: number | null;
  minDays: number | null;
  // Variable pricing (native currency). Rows are edited in place; a
  // partially-filled row blocks the save (see incompleteVariablePricing).
  durationTiers: Array<{ minDays: number | null; maxDays: number | null; pricePerDay: number | null }>;
  priceExceptions: Array<{ dateFrom: string; dateTo: string; pricePerDay: number | null; label: string }>;
  instantBooking: boolean;
  defaultState: AdminCarBookingDefaultState | '';
  // v2
  attributeOptions: RelationshipOption[];
  pickupAirportOptions: RelationshipOption[];
  youtubeUrl: string;
  icalImportUrl: string;
  icalExportUrl: string;
  extraInfoPassenger: string;
  extraInfoGearShift: string;
  extraInfoBaggage: string;
  extraInfoDoors: string;
  // Per-locale title / description / SEO meta (cars_locales), editable in-app.
  locales: Record<CarLocale, CarLocaleFields>;
}

type CarLocale = 'en' | 'fr' | 'ar' | 'es';
const CAR_LOCALES: CarLocale[] = ['en', 'fr', 'ar', 'es'];
interface CarLocaleFields {
  title: string;
  content: string;
  seoMetaTitle: string;
  seoMetaDescription: string;
}
function localeFieldsFrom(c: AdminCarDetail, l: CarLocale): CarLocaleFields {
  return {
    title: c.localised.title[l] ?? '',
    content: c.localised.content[l] ?? '',
    seoMetaTitle: c.localised.seoMetaTitle[l] ?? '',
    seoMetaDescription: c.localised.seoMetaDescription[l] ?? '',
  };
}

function carToDraft(c: AdminCarDetail): CarDraft {
  return {
    slug: c.slug,
    status: c.status,
    isFeatured: c.isFeatured,
    number: c.number,
    vendorId: c.vendorId,
    locationId: c.locationId,
    vendorOption: vendorAsOption(c),
    locationOption: locationAsOption(c),
    address: c.address ?? '',
    mapLatitude: c.map?.latitude ?? null,
    mapLongitude: c.map?.longitude ?? null,
    mapZoom: c.map?.zoom ?? null,
    price: c.pricing.price,
    salePrice: c.pricing.salePrice,
    unit: c.pricing.unit,
    currency: c.pricing.currency ?? 'EUR',
    securityDeposit: c.pricing.securityDeposit,
    minDays: c.pricing.minDays,
    durationTiers: c.durationTiers.map((t) => ({ ...t })),
    priceExceptions: c.priceExceptions.map((e) => ({
      dateFrom: e.dateFrom,
      dateTo: e.dateTo,
      pricePerDay: e.pricePerDay,
      label: e.label ?? '',
    })),
    instantBooking: c.booking.instantBooking,
    defaultState: c.booking.defaultState ?? '',
    attributeOptions: c.attributeValues.map((av) => ({
      id: av.id,
      label: av.name ?? av.slug,
      sub: av.attributeName ?? av.attributeSlug,
    })),
    pickupAirportOptions: c.pickupAirports.map((a) => ({
      id: a.id,
      label: a.name,
      sub: a.type ?? undefined,
    })),
    youtubeUrl: c.youtubeUrl ?? '',
    icalImportUrl: c.icalImportUrl ?? '',
    icalExportUrl: c.icalExportUrl ?? '',
    extraInfoPassenger: c.extraInfo.passenger ?? '',
    extraInfoGearShift: c.extraInfo.gearShift ?? '',
    extraInfoBaggage: c.extraInfo.baggage ?? '',
    extraInfoDoors: c.extraInfo.doors ?? '',
    locales: Object.fromEntries(
      CAR_LOCALES.map((l) => [l, localeFieldsFrom(c, l)]),
    ) as Record<CarLocale, CarLocaleFields>,
  };
}

function vendorAsOption(c: AdminCarDetail): RelationshipOption {
  const label =
    c.vendor.businessName ??
    [c.vendor.firstName, c.vendor.lastName].filter(Boolean).join(' ') ??
    c.vendor.email ??
    `Vendor #${c.vendor.id}`;
  return { id: c.vendor.id, label, sub: c.vendor.email };
}

function locationAsOption(c: AdminCarDetail): RelationshipOption | null {
  if (c.locationId == null) return null;
  return {
    id: c.locationId,
    label: c.location?.name ?? c.location?.slug ?? `Location #${c.locationId}`,
    sub: c.location?.slug ?? null,
  };
}

// A partially-filled variable-pricing row must block the save: the PATCH is
// full-replace, so silently dropping it would DELETE existing rows. Rows that
// are entirely empty (just added, untouched) don't count.
function incompleteVariablePricing(d: CarDraft): boolean {
  const tierBad = d.durationTiers.some((t) => {
    const empty = t.minDays == null && t.maxDays == null && t.pricePerDay == null;
    const complete = t.minDays != null && t.pricePerDay != null;
    return !empty && !complete;
  });
  const exceptionBad = d.priceExceptions.some((e) => {
    const empty = e.dateFrom === '' && e.dateTo === '' && e.pricePerDay == null && e.label === '';
    const complete = e.dateFrom !== '' && e.dateTo !== '' && e.pricePerDay != null;
    return !empty && !complete;
  });
  return tierBad || exceptionBad;
}

// Build the PATCH payload from the diff between current row + draft.
function buildPatch(c: AdminCarDetail, d: CarDraft): CarPatchInput {
  const p: CarPatchInput = {};
  if (d.slug.trim() !== c.slug) p.slug = d.slug.trim();
  if (d.status !== c.status) p.status = d.status;
  if (d.isFeatured !== c.isFeatured) p.isFeatured = d.isFeatured;
  if (d.number !== c.number) {
    if (d.number != null) p.number = d.number;
  }
  if (d.vendorId !== c.vendorId) p.vendorId = d.vendorId;
  if (d.locationId !== c.locationId) {
    if (d.locationId != null) p.locationId = d.locationId;
  }
  const addr = d.address.trim() === '' ? null : d.address.trim();
  if (addr !== c.address) p.address = addr;
  // Map fields — send the trio together as they share a section.
  const mapDirty =
    d.mapLatitude !== (c.map?.latitude ?? null) ||
    d.mapLongitude !== (c.map?.longitude ?? null) ||
    d.mapZoom !== (c.map?.zoom ?? null);
  if (mapDirty) {
    p.map = {
      latitude: d.mapLatitude,
      longitude: d.mapLongitude,
      zoom: d.mapZoom,
    };
  }
  // Pricing.
  const pricing: NonNullable<CarPatchInput['pricing']> = {};
  if (d.price !== c.pricing.price && d.price != null) pricing.price = d.price;
  if (d.salePrice !== c.pricing.salePrice) pricing.salePrice = d.salePrice;
  if (d.unit !== c.pricing.unit) pricing.unit = d.unit;
  if (d.currency !== (c.pricing.currency ?? 'EUR')) pricing.currency = d.currency;
  if (d.securityDeposit !== c.pricing.securityDeposit) pricing.securityDeposit = d.securityDeposit;
  if (d.minDays !== c.pricing.minDays) pricing.minDays = d.minDays;

  // Variable pricing — complete rows only, sorted like the server returns
  // them (ORDER BY min_days) so an out-of-order but identical set isn't a
  // false dirty. Partially-filled rows BLOCK the save (see
  // incompleteVariablePricing) rather than being silently dropped — dropping
  // them would delete existing rows via the full-replace PATCH.
  const draftTiers = d.durationTiers
    .filter((t) => t.minDays != null && t.pricePerDay != null)
    .map((t) => ({
      minDays: Math.max(1, Math.round(t.minDays!)),
      maxDays: t.maxDays == null ? null : Math.max(1, Math.round(t.maxDays)),
      pricePerDay: t.pricePerDay!,
    }))
    .sort((a, b) => a.minDays - b.minDays);
  if (JSON.stringify(draftTiers) !== JSON.stringify(c.durationTiers)) {
    p.durationTiers = draftTiers;
  }
  const draftExceptions = d.priceExceptions
    .filter((e) => e.dateFrom !== '' && e.dateTo !== '' && e.pricePerDay != null)
    .map((e) => ({
      dateFrom: e.dateFrom,
      dateTo: e.dateTo,
      pricePerDay: e.pricePerDay!,
      label: e.label.trim() === '' ? null : e.label.trim(),
    }));
  if (
    JSON.stringify(draftExceptions) !==
    JSON.stringify(c.priceExceptions.map((e) => ({ ...e, label: e.label ?? null })))
  ) {
    p.priceExceptions = draftExceptions;
  }
  if (Object.keys(pricing).length > 0) p.pricing = pricing;
  // Booking.
  const booking: NonNullable<CarPatchInput['booking']> = {};
  if (d.instantBooking !== c.booking.instantBooking) booking.instantBooking = d.instantBooking;
  const ds = d.defaultState === '' ? null : d.defaultState;
  if (ds !== (c.booking.defaultState ?? null)) booking.defaultState = ds;
  if (Object.keys(booking).length > 0) p.booking = booking;

  // Attribute values — wholesale replace when set differs (regardless of order).
  const draftAttrIds = d.attributeOptions.map((o) => o.id).sort((a, b) => a - b);
  const currentAttrIds = c.attributeValues.map((a) => a.id).sort((a, b) => a - b);
  if (
    draftAttrIds.length !== currentAttrIds.length ||
    draftAttrIds.some((id, i) => id !== currentAttrIds[i])
  ) {
    p.attributeValueIds = d.attributeOptions.map((o) => o.id);
  }

  // Pick-up airports — wholesale replace when the set differs.
  const draftAirportIds = d.pickupAirportOptions.map((o) => o.id).sort((a, b) => a - b);
  const currentAirportIds = c.pickupAirports.map((a) => a.id).sort((a, b) => a - b);
  if (
    draftAirportIds.length !== currentAirportIds.length ||
    draftAirportIds.some((id, i) => id !== currentAirportIds[i])
  ) {
    p.pickupAirportIds = d.pickupAirportOptions.map((o) => o.id);
  }

  // Free-form text fields — empty string → null on the wire.
  const yt = d.youtubeUrl.trim() === '' ? null : d.youtubeUrl.trim();
  if (yt !== c.youtubeUrl) p.youtubeUrl = yt;
  const ical = d.icalImportUrl.trim() === '' ? null : d.icalImportUrl.trim();
  if (ical !== c.icalImportUrl) p.icalImportUrl = ical;
  const icalEx = d.icalExportUrl.trim() === '' ? null : d.icalExportUrl.trim();
  if (icalEx !== c.icalExportUrl) p.icalExportUrl = icalEx;

  // Extra info — same null-on-empty rule.
  const extra: NonNullable<CarPatchInput['extraInfo']> = {};
  const ePass = d.extraInfoPassenger.trim() === '' ? null : d.extraInfoPassenger.trim();
  if (ePass !== c.extraInfo.passenger) extra.passenger = ePass;
  const eGear = d.extraInfoGearShift.trim() === '' ? null : d.extraInfoGearShift.trim();
  if (eGear !== c.extraInfo.gearShift) extra.gearShift = eGear;
  const eBag = d.extraInfoBaggage.trim() === '' ? null : d.extraInfoBaggage.trim();
  if (eBag !== c.extraInfo.baggage) extra.baggage = eBag;
  const eDoors = d.extraInfoDoors.trim() === '' ? null : d.extraInfoDoors.trim();
  if (eDoors !== c.extraInfo.doors) extra.doors = eDoors;
  if (Object.keys(extra).length > 0) p.extraInfo = extra;

  // Per-locale fields — ship only locales whose any field changed.
  const locales: NonNullable<CarPatchInput['locales']> = {};
  for (const l of CAR_LOCALES) {
    const orig = localeFieldsFrom(c, l);
    if (JSON.stringify(d.locales[l]) !== JSON.stringify(orig)) {
      locales[l] = {
        title: d.locales[l].title.trim(),
        content: d.locales[l].content,
        seoMetaTitle: d.locales[l].seoMetaTitle,
        seoMetaDescription: d.locales[l].seoMetaDescription,
      };
    }
  }
  if (Object.keys(locales).length > 0) p.locales = locales;

  return p;
}

// ─── Page ─────────────────────────────────────────────────────────────

export function CarDetail() {
  // `strict: false` matches whatever route is currently active, sidestepping
  // TanStack Router's runtime invariant that fires on `from`-mismatch when the
  // pathless `authed` parent rewrites the full route id (TSR ≥1.165).
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'cars', id],
    queryFn: ({ signal }) => fetchAdminCarDetail(id, signal),
  });

  const [draft, setDraft] = useState<CarDraft | null>(null);
  // Refetches rebuild the draft ONLY when the operator has no unsaved edits.
  // "Edits" must be measured against the data snapshot the draft was BUILT
  // FROM (draftBaseRef) — not against the freshly fetched data. Comparing to
  // fresh data made any server-side change (the AI copy fill, another
  // operator's save) look like local edits, which blocked the rebuild and
  // left the form showing stale empty fields after a successful generate.
  const draftBaseRef = useRef<AdminCarDetail | null>(null);
  const draftRef = useRef<CarDraft | null>(null);
  draftRef.current = draft;
  useEffect(() => {
    if (!data) return;
    const base = draftBaseRef.current;
    const hasUserEdits =
      base != null &&
      draftRef.current != null &&
      Object.keys(buildPatch(base, draftRef.current)).length > 0;
    if (!hasUserEdits) {
      draftBaseRef.current = data;
      setDraft(carToDraft(data));
    }
  }, [data]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'cars'] });

  const approveMut = useMutation({ mutationFn: approveCar, onSuccess: invalidate });
  const rejectMut = useMutation({
    mutationFn: ({ id: i, reason }: { id: number; reason?: string }) => rejectCar(i, reason),
    onSuccess: invalidate,
  });
  const archiveMut = useMutation({ mutationFn: archiveCar, onSuccess: invalidate });
  const featureMut = useMutation({ mutationFn: featureCar, onSuccess: invalidate });
  const unfeatureMut = useMutation({ mutationFn: unfeatureCar, onSuccess: invalidate });

  const saveMut = useMutation({
    mutationFn: (body: CarPatchInput) => patchCar(id, body),
    onSuccess: () => {
      // The draft's edits are now server truth — drop the baseline so the
      // post-save refetch rebuilds draft + baseline from fresh data.
      draftBaseRef.current = null;
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'cars', id] });
    },
  });

  // City-specific AI copy — fills only empty description/SEO fields server-
  // side, then refetches; the effect above rebuilds the draft with the new
  // values. Unsaved edits are SAVED first by the button handler (clearing a
  // field then clicking is the natural "regenerate this field" gesture — the
  // save makes the cleared state visible to the server-side fill).
  const aiCopyMut = useMutation({
    mutationFn: () => retrieveCarCopy(id),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['admin', 'cars', id] }),
  });
  const [aiBusy, setAiBusy] = useState(false);

  if (isLoading || !draft) {
    return (
      <DetailLayout title="Loading…" backTo="/cars" backLabel="All cars">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading car…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/cars" backLabel="All cars">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No car with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const c = data;
  const moderationBusy =
    approveMut.isPending ||
    rejectMut.isPending ||
    archiveMut.isPending ||
    featureMut.isPending ||
    unfeatureMut.isPending;

  const patch = buildPatch(c, draft);
  const dirty = Object.keys(patch).length > 0;
  const vpIncomplete = incompleteVariablePricing(draft);
  const saveBlocked = vpIncomplete
    ? new Error('Complete or remove the partially-filled pricing rows before saving.')
    : null;
  const trySave = () => {
    if (!saveBlocked) saveMut.mutate(patch);
  };

  const setField = <K extends keyof CarDraft>(k: K, v: CarDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  const setLocaleField = (l: CarLocale, k: keyof CarLocaleFields, v: string) =>
    setDraft((d) =>
      d ? { ...d, locales: { ...d.locales, [l]: { ...d.locales[l], [k]: v } } } : d,
    );

  // Header shows the CANONICAL Car title (vehicle-catalog resolution done by
  // the API); the raw vendor-entered listing title surfaces as secondary info
  // only when it differs. The Translations editor below still edits the raw
  // per-locale titles untouched.
  const rawTitleEN = (c.listingTitle ?? c.localised.title.en)?.trim() || '';
  const headerTitle = c.title?.trim() || rawTitleEN || c.slug;
  const showVendorTitle = Boolean(rawTitleEN) && rawTitleEN !== headerTitle;

  return (
    <DetailLayout
      eyebrow="Operate · Car"
      title={headerTitle}
      sub={`${c.location?.name ?? c.location?.slug ?? ''} · ${fmtMoney(c.pricing.price)} / ${c.pricing.unit}${
        showVendorTitle ? ` · vendor: ${rawTitleEN}` : ''
      }`}
      backTo="/cars"
      backLabel="All cars"
      actions={
        <>
          {c.status === 'pending' || c.status === 'draft' ? (
            <RowAction kind="primary" busy={moderationBusy} onClick={() => approveMut.mutate(c.id)}>
              Approve
            </RowAction>
          ) : null}
          {c.status === 'pending' ? (
            <RowAction
              kind="danger"
              busy={moderationBusy}
              onClick={() => {
                const reason = window.prompt('Rejection reason (optional):') ?? undefined;
                rejectMut.mutate({ id: c.id, reason });
              }}
            >
              Reject
            </RowAction>
          ) : null}
          {c.status === 'published' ? (
            <RowAction
              busy={moderationBusy}
              onClick={() => (c.isFeatured ? unfeatureMut.mutate(c.id) : featureMut.mutate(c.id))}
            >
              {c.isFeatured ? 'Unfeature' : 'Feature'}
            </RowAction>
          ) : null}
          {c.status !== 'archived' ? (
            <RowAction busy={moderationBusy} onClick={() => archiveMut.mutate(c.id)}>
              Archive
            </RowAction>
          ) : null}
          <StatusPill status={c.status} />
        </>
      }
    >
      <TranslationsEditor
        draft={draft}
        catalogLocales={c.catalogLocales}
        onChange={setLocaleField}
        ai={{
          run: () => {
            void (async () => {
              setAiBusy(true);
              try {
                // Save unsaved edits first (a cleared field must reach the
                // server so fill-only-empty can refill it), then generate.
                if (dirty) await saveMut.mutateAsync(patch);
                await aiCopyMut.mutateAsync();
              } catch {
                // Surfaced via saveMut error (SaveBar) / aiCopyMut error below.
              } finally {
                setAiBusy(false);
              }
            })();
          },
          pending: aiBusy || aiCopyMut.isPending,
          dirty,
          error: aiCopyMut.error ? aiErrorMessage(aiCopyMut.error) : null,
          filled: aiCopyMut.data?.filled ?? null,
        }}
        canonicalTitle={c.title ?? c.slug}
      />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        error={saveBlocked ?? saveMut.error}
        success={saveMut.isSuccess && !dirty}
        onReset={() => setDraft(carToDraft(c))}
        onSave={trySave}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DetailSection title="Identity">
          <div className="space-y-4">
            <TextField
              label="Slug"
              monospace
              required
              value={draft.slug}
              onChange={(v) => setField('slug', v)}
              hint="URL-safe identifier used in /cars/<slug>. Kebab-case only."
            />
            <SelectField
              label="Status"
              required
              value={draft.status}
              onChange={(v) => setField('status', v)}
              options={STATUS_OPTIONS}
              hint="Storefront only renders `published`. Pending/draft are admin-only."
            />
            <BooleanField
              label="Featured"
              hint="Surfaces this car on the home page Featured grid."
              checked={draft.isFeatured}
              onChange={(v) => setField('isFeatured', v)}
            />
            <NumberField
              label="Fleet size"
              value={draft.number}
              onChange={(v) => setField('number', v == null ? 1 : Math.max(1, Math.floor(v)))}
              min={1}
              max={9999}
              step={1}
              hint="How many identical units the vendor owns. Search subtracts overlapping bookings."
            />
          </div>
        </DetailSection>

        <DetailSection title="Pricing">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                label="Currency"
                value={draft.currency}
                onChange={(v) => setField('currency', v)}
                options={CURRENCY_OPTIONS}
              />
              <SelectField
                label="Unit"
                value={draft.unit}
                onChange={(v) => setField('unit', v)}
                options={UNIT_OPTIONS}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Price"
                required
                value={draft.price}
                onChange={(v) => setField('price', v)}
                min={0}
                step={1}
                prefix={currencySymbol(draft.currency)}
                suffix={draft.unit === 'hour' ? '/hr' : '/day'}
              />
              <NumberField
                label="Sale price"
                value={draft.salePrice}
                onChange={(v) => setField('salePrice', v)}
                min={0}
                step={1}
                prefix={currencySymbol(draft.currency)}
                hint="Optional — displayed as a strike-through."
              />
            </div>
            <NumberField
              label="Security deposit"
              value={draft.securityDeposit}
              onChange={(v) => setField('securityDeposit', v)}
              min={0}
              step={1}
              prefix={currencySymbol(draft.currency)}
              hint="Held by the vendor; refunded on return. Optional."
            />
            <NumberField
              label="Minimum rental (days)"
              value={draft.minDays}
              onChange={(v) =>
                // Whole days >= 1 — the save path has no form submit, so the
                // input's min/step never run; clamp here or a 0/2.5 400s.
                setField('minDays', v == null ? null : Math.max(1, Math.round(v)))
              }
              min={1}
              step={1}
              hint="This listing's own floor — shorter bookings are rejected. Empty = only the platform-wide minimum from Booking settings applies."
            />
            <VariablePricingEditor
              currency={draft.currency}
              tiers={draft.durationTiers}
              exceptions={draft.priceExceptions}
              onTiersChange={(next) => setField('durationTiers', next)}
              onExceptionsChange={(next) => setField('priceExceptions', next)}
            />
          </div>
        </DetailSection>

        <DetailSection title="Owner & pickup">
          <div className="space-y-4">
            <RelationshipPicker
              label="Vendor"
              required
              value={draft.vendorId}
              valueLabel={draft.vendorOption?.label}
              valueSub={draft.vendorOption?.sub ?? null}
              onChange={(id, opt) => {
                if (id == null) return; // vendor required — keep current
                setField('vendorId', id);
                setField('vendorOption', opt ?? null);
              }}
              searchFn={async (q, signal) => {
                const res = await fetchAdminVendors({ q, limit: 20 }, signal);
                return res.rows.map((r) => ({
                  id: r.id,
                  label: r.businessName ?? r.displayName,
                  sub: r.email,
                }));
              }}
              hint="Owner of the car. Must be a User with role=vendor or admin."
            />
            <LocationPicker
              label="Pickup location"
              value={draft.locationId}
              valueLabel={draft.locationOption?.label}
              valueSub={draft.locationOption?.sub ?? null}
              onChange={(id, opt) => {
                setField('locationId', id);
                setField('locationOption', opt ?? null);
              }}
              searchFn={async (q, signal) => {
                const res = await fetchAdminLocations({ q, limit: 20 }, signal);
                return res.rows.map((r) => ({
                  id: r.id,
                  label: r.name ?? r.slug,
                  sub: `${r.type}${r.countryCode ? ` · ${r.countryCode}` : ''}`,
                }));
              }}
              hint="City / airport / station. Used by the country tabs on the home page."
            />
          </div>
        </DetailSection>

        <DetailSection title="Place">
          <div className="space-y-4">
            <TextAreaField
              label="Address"
              value={draft.address}
              onChange={(v) => setField('address', v)}
              rows={2}
              hint="Free-form pickup address. Shown on the booking confirmation."
            />
            <div className="grid grid-cols-3 gap-3">
              <NumberField
                label="Latitude"
                value={draft.mapLatitude}
                onChange={(v) => setField('mapLatitude', v)}
                min={-90}
                max={90}
                step={0.000001}
              />
              <NumberField
                label="Longitude"
                value={draft.mapLongitude}
                onChange={(v) => setField('mapLongitude', v)}
                min={-180}
                max={180}
                step={0.000001}
              />
              <NumberField
                label="Zoom"
                value={draft.mapZoom}
                onChange={(v) => setField('mapZoom', v)}
                min={0}
                max={22}
                step={1}
              />
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Booking">
          <div className="space-y-4">
            <BooleanField
              label="Instant booking"
              hint="When ON, customers complete checkout immediately. When OFF, vendor must confirm."
              checked={draft.instantBooking}
              onChange={(v) => setField('instantBooking', v)}
            />
            <SelectField
              label="Default availability state"
              value={draft.defaultState}
              onChange={(v) => setField('defaultState', v)}
              options={BOOKING_STATE_OPTIONS}
              hint="Override the per-day default. Usually leave blank — calendar is per-day."
            />
          </div>
        </DetailSection>

        <DetailSection title="Specs">
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Passengers"
              value={
                draft.extraInfoPassenger.trim() === ''
                  ? null
                  : Number(draft.extraInfoPassenger)
              }
              onChange={(v) =>
                setField('extraInfoPassenger', v == null ? '' : String(v))
              }
              min={0}
              step={1}
            />
            <TextField
              label="Gear shift"
              value={draft.extraInfoGearShift}
              onChange={(v) => setField('extraInfoGearShift', v)}
              placeholder="manual / auto"
            />
            <NumberField
              label="Baggage"
              value={
                draft.extraInfoBaggage.trim() === ''
                  ? null
                  : Number(draft.extraInfoBaggage)
              }
              onChange={(v) =>
                setField('extraInfoBaggage', v == null ? '' : String(v))
              }
              min={0}
              step={1}
            />
            <NumberField
              label="Doors"
              value={
                draft.extraInfoDoors.trim() === ''
                  ? null
                  : Number(draft.extraInfoDoors)
              }
              onChange={(v) => setField('extraInfoDoors', v == null ? '' : String(v))}
              min={0}
              step={1}
            />
          </div>
        </DetailSection>

        <DetailSection title="Attributes">
          <MultiRelationshipPicker
            label="Attribute values"
            value={draft.attributeOptions}
            onChange={(next) => setField('attributeOptions', next)}
            searchFn={async (q, signal) => {
              const res = await fetchAdminAttributeValues({ q, limit: 50 }, signal);
              return res.rows.map((r) => ({
                id: r.id,
                label: r.name ?? r.slug,
                sub: r.attributeName ?? r.attributeSlug,
              }));
            }}
            placeholder="Search transmission, fuel, AC, …"
            hint="Multi-select. Removed values are unlinked on save; a full set replacement is sent."
          />
        </DetailSection>

        <DetailSection title="Pick-up airports">
          <MultiRelationshipPicker
            label="Airports & stations"
            value={draft.pickupAirportOptions}
            onChange={(next) => setField('pickupAirportOptions', next)}
            searchFn={async (q, signal) => {
              const [airports, stations] = await Promise.all([
                fetchAdminLocations({ q, type: 'airport', limit: 25 }, signal),
                fetchAdminLocations({ q, type: 'station', limit: 25 }, signal),
              ]);
              return [...airports.rows, ...stations.rows].map((r) => ({
                id: r.id,
                label: r.name ?? r.slug,
                sub: `${r.type}${r.countryCode ? ` · ${r.countryCode}` : ''}`,
              }));
            }}
            placeholder="Search an airport or station…"
            hint="Terminals a customer can collect this car from. Storefront 'Pickup at airport' filters by these. Leaving it empty removes the car from any terminal-specific search."
          />
        </DetailSection>

        <DetailSection title="Embeds & sync">
          <div className="space-y-4">
            <TextField
              label="YouTube URL"
              type="url"
              value={draft.youtubeUrl}
              onChange={(v) => setField('youtubeUrl', v)}
              placeholder="https://youtube.com/watch?v=…"
              hint="Optional walkaround / promo video. Empty string clears the field."
            />
            <TextField
              label="iCal import URL"
              type="url"
              value={draft.icalImportUrl}
              onChange={(v) => setField('icalImportUrl', v)}
              placeholder="https://…/calendar.ics"
              hint="Vendor calendar to read blackout dates from. We poll this periodically."
            />
            <TextField
              label="iCal export URL"
              type="url"
              value={draft.icalExportUrl}
              onChange={(v) => setField('icalExportUrl', v)}
              placeholder="https://…/export.ics"
              hint="Read-only export of this car's bookings, for the vendor's own calendar app. Usually system-generated — leave empty to use the default."
            />
          </div>
        </DetailSection>

        <DetailSection title="Reviews (read-only)">
          <DefRow label="Count">{c.reviewCount}</DefRow>
          <DefRow label="Average">
            {c.reviewScore == null ? '—' : c.reviewScore.toFixed(2)}
          </DefRow>
        </DetailSection>
      </div>

      <CatalogPhotosSection car={c} />

      <DetailSection title="Vendor (read-only)">
        <DefRow label="Business">{c.vendor.businessName ?? '—'}</DefRow>
        <DefRow label="Email">{c.vendor.email ?? '—'}</DefRow>
        <DefRow label="Name">
          {[c.vendor.firstName, c.vendor.lastName].filter(Boolean).join(' ') || '—'}
        </DefRow>
        <button
          type="button"
          onClick={() =>
            void navigate({
              to: '/crew/$id' as never,
              params: { id: String(c.vendor.id) } as never,
            })
          }
          className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
        >
          View vendor →
        </button>
      </DetailSection>

      <EntityActivity entityType="cars" entityId={c.id} title="Audit trail" />

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        error={saveBlocked ?? saveMut.error}
        success={saveMut.isSuccess && !dirty}
        onReset={() => setDraft(carToDraft(c))}
        onSave={trySave}
      />
    </DetailLayout>
  );
}

function aiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    if (code === 'ai_not_configured') return 'AI is not configured yet.';
    if (code === 'no_vehicle_identity')
      return 'This listing has no vehicle identity yet — link a catalog Car first.';
  }
  return 'Generate failed. Try again.';
}

// ─── Translations editor (cars_locales: title / description / SEO) ──────

function TranslationsEditor({
  draft,
  catalogLocales,
  canonicalTitle,
  onChange,
  ai,
}: {
  draft: CarDraft;
  catalogLocales: AdminCarDetail['catalogLocales'];
  /** Resolved display title (catalog locale → catalog default → raw listing).
   *  Placeholder of last resort so the Title box never looks broken-empty. */
  canonicalTitle: string;
  onChange: (l: CarLocale, k: keyof CarLocaleFields, v: string) => void;
  ai?: {
    run: () => void;
    pending: boolean;
    /** Unsaved edits exist — the run will save them first, then generate. */
    dirty: boolean;
    error: string | null;
    /** Locales the last run filled; [] = nothing was empty. Null = not run. */
    filled: string[] | null;
  };
}) {
  const [lang, setLang] = useState<CarLocale>('en');
  const f = draft.locales[lang];
  // Canonical Car copy for the active locale — the storefront default when the
  // listing's own fields are empty.
  const inherited = catalogLocales?.[lang];
  const snippet = (v: string | null | undefined, max = 160) =>
    v && v.length > max ? `${v.slice(0, max)}…` : (v ?? '');
  // Effective per-locale name shown as the Title placeholder: catalog locale
  // title → canonical resolved title. Never empty for a real listing.
  const effectiveTitle = (l: CarLocale) =>
    catalogLocales?.[l]?.title?.trim() || canonicalTitle;
  return (
    <DetailSection title="Translations & description">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {CAR_LOCALES.map((l) => {
            // Warning dot only when the locale has NO effective name at all —
            // an empty raw title with a catalog name behind it is by design
            // (names are DriveCars-managed), not missing content.
            const filled =
              draft.locales[l].title.trim().length > 0 || effectiveTitle(l).length > 0;
            return (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={`inline-flex h-8 items-center gap-1 rounded-md border px-3 text-[12px] font-medium ${
                  l === lang
                    ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                    : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
                }`}
              >
                {l.toUpperCase()}
                {!filled ? <span className="text-[#d97706]">•</span> : null}
              </button>
            );
          })}
        </div>
        {ai ? (
          <button
            type="button"
            onClick={ai.run}
            disabled={ai.pending}
            title={
              (ai.dirty ? 'Saves your edits first, then generates. ' : '') +
              'Fills the EMPTY description + SEO fields in all four languages with city-specific copy. Existing text is kept — clear a field and click to regenerate it. Also runs automatically on approve.'
            }
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--bo-line)] bg-white px-3 text-[12px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {ai.pending ? 'Generating…' : ai.dirty ? 'Save & generate with AI' : 'Generate with AI'}
          </button>
        ) : null}
      </div>
      {ai?.error ? (
        <div className="mb-3 rounded-md border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12px] text-[#9f1239]">
          {ai.error}
        </div>
      ) : null}
      {ai && !ai.error && ai.filled ? (
        <p className="mb-3 text-[11px] text-[color:var(--bo-mute)]">
          {ai.filled.length > 0
            ? `AI filled the empty fields in: ${ai.filled.map((l) => l.toUpperCase()).join(', ')}.`
            : 'Nothing to fill — every locale already has copy.'}
        </p>
      ) : null}
      <div className="space-y-4">
        <TextField
          label="Title"
          value={f.title}
          onChange={(v) => onChange(lang, 'title', v)}
          placeholder={!f.title ? effectiveTitle(lang) : undefined}
          hint={`Names are DriveCars-managed — the storefront shows "${snippet(effectiveTitle(lang), 80)}" for this locale. This raw listing title is a legacy fallback only; AI generation deliberately never writes it.`}
        />
        <TextAreaField
          label="Description"
          value={f.content}
          onChange={(v) => onChange(lang, 'content', v)}
          placeholder={!f.content && inherited?.description ? snippet(inherited.description) : undefined}
          hint={
            inherited?.description
              ? f.content
                ? 'Overrides the catalog Car description for this listing.'
                : 'Empty — the storefront shows the catalog Car description (previewed above). Write here to override it for this listing, e.g. city-specific copy.'
              : undefined
          }
        />
        <TextField
          label="SEO meta title"
          value={f.seoMetaTitle}
          onChange={(v) => onChange(lang, 'seoMetaTitle', v)}
        />
        <TextAreaField
          label="SEO meta description"
          value={f.seoMetaDescription}
          onChange={(v) => onChange(lang, 'seoMetaDescription', v)}
          hint={
            !f.seoMetaDescription
              ? 'Empty — the public page falls back to an auto-generated meta description.'
              : undefined
          }
        />
      </div>
    </DetailSection>
  );
}

// ─── Save bar ──────────────────────────────────────────────────────

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Photos (DriveCars-managed on the catalog Car) ────────────────
//
// Listings no longer carry their own hero/gallery uploads — images live on
// the linked vehicle_catalog_entries row and every listing of that Car shares
// them. This panel previews the entry's images and links to the catalog
// editor. Legacy listing media (cars.image_id / cars_gallery) still renders
// on unlinked listings as a read-only fallback.

// cms.media.url is relative (/api/media/file/<name>) when MEDIA_CDN_URL is
// unset (local dev) — resolve against the Payload origin, not the backoffice.
function resolveMediaUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

function CatalogPhotosSection({ car }: { car: AdminCarDetail }) {
  const navigate = useNavigate();
  // Same queryKey the /vehicle-catalog editor uses, so following the link
  // lands on a pre-warmed cache.
  const entryQ = useQuery({
    queryKey: ['admin', 'vehicle-catalog', 'entry', car.carId],
    queryFn: ({ signal }) => fetchCatalogEntry(car.carId!, signal),
    enabled: car.carId != null,
  });

  if (car.carId == null) {
    return (
      <DetailSection title="Photos">
        <p className="text-[12.5px] text-[color:var(--bo-mute)]">
          No catalog Car linked — photos are DriveCars-managed on catalog entries, so this legacy
          listing only shows its old media below. Link it to a catalog Car (via Needs linking) to
          manage photos.
        </p>
        {car.imageUrl || car.gallery.length > 0 ? (
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
            {[
              ...(car.imageUrl ? [{ id: -1, url: car.imageUrl, alt: 'hero' }] : []),
              ...car.gallery,
            ].map((g) => {
              const url = resolveMediaUrl(g.url);
              return (
                <div
                  key={g.id}
                  className="relative aspect-[4/3] overflow-hidden rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]"
                >
                  {url ? (
                    <img src={url} alt={g.alt ?? ''} className="h-full w-full object-cover" />
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </DetailSection>
    );
  }

  const images = entryQ.data?.images ?? [];
  const hero = images.find((i) => i.isHero) ?? images[0] ?? null;
  const heroUrl = resolveMediaUrl(hero?.url);

  return (
    <DetailSection title="Photos">
      <div className="flex flex-wrap items-start gap-4">
        <div className="relative aspect-[4/3] w-44 shrink-0 overflow-hidden rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]">
          {heroUrl ? (
            <img src={heroUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-[color:var(--bo-mute)]">
              {entryQ.isLoading ? 'Loading…' : 'No photos on the catalog Car yet'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-[12.5px] text-[color:var(--bo-ink)]">
            Photos are managed on the catalog Car
            {entryQ.data ? (
              <>
                {' '}
                — <strong>{entryQ.data.defaultTitle}</strong> ({images.length} image
                {images.length === 1 ? '' : 's'})
              </>
            ) : null}
            . They apply to every listing of this Car across search, availability and the public
            page.
          </p>
          {images.length > 0 && !images.some((i) => i.isHero) ? (
            <p className="text-[12px] text-[#d97706]">
              No image is flagged as hero — lists and search read the hero only, so this listing
              shows without a photo until one is set.
            </p>
          ) : null}
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/vehicle-catalog' as never,
                search: { entry: car.carId } as never,
              })
            }
            className="text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            Manage photos on the catalog Car →
          </button>
        </div>
      </div>
    </DetailSection>
  );
}

// ─── Variable pricing editor (duration tiers + date exceptions) ──────
//
// Rates are per-day HOST BASE in the listing's native currency — the platform
// markup applies on top, same as the flat price. Day rate resolution:
// exception covering the day > tier for the stay's total length > flat price.

type TierRow = CarDraft['durationTiers'][number];
type ExceptionRow = CarDraft['priceExceptions'][number];

function VariablePricingEditor({
  currency,
  tiers,
  exceptions,
  onTiersChange,
  onExceptionsChange,
}: {
  currency: string;
  tiers: TierRow[];
  exceptions: ExceptionRow[];
  onTiersChange: (next: TierRow[]) => void;
  onExceptionsChange: (next: ExceptionRow[]) => void;
}) {
  const field =
    'h-8 w-full rounded-md border border-[color:var(--bo-line)] bg-white px-2 text-[12.5px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)]';
  const label = 'text-[11px] font-medium text-[color:var(--bo-mute)]';
  const numOrNull = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const setTier = (idx: number, patch: Partial<TierRow>) =>
    onTiersChange(tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  const setException = (idx: number, patch: Partial<ExceptionRow>) =>
    onExceptionsChange(exceptions.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  return (
    <div className="space-y-5 border-t border-[color:var(--bo-line)] pt-4">
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">
            Duration pricing (per-day rate by rental length)
          </span>
          <button
            type="button"
            onClick={() =>
              onTiersChange([...tiers, { minDays: null, maxDays: null, pricePerDay: null }])
            }
            className="text-[11.5px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            + Add tier
          </button>
        </div>
        <p className="mb-2 text-[11px] text-[color:var(--bo-mute)]">
          e.g. 1–3 days at {currency} 30/day, 4–7 at 40, 8+ (leave “to” empty) at 28. Bands must
          not overlap. Empty = the flat price applies to every length.
        </p>
        {tiers.length > 0 ? (
          <ul className="space-y-1.5">
            {tiers.map((t, idx) => (
              <li key={idx} className="grid grid-cols-[1fr_1fr_1.4fr_auto] items-end gap-2">
                <label className="block space-y-0.5">
                  <span className={label}>From (days)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={t.minDays ?? ''}
                    onChange={(e) => setTier(idx, { minDays: numOrNull(e.target.value) })}
                    className={field}
                  />
                </label>
                <label className="block space-y-0.5">
                  <span className={label}>To (empty = ∞)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={t.maxDays ?? ''}
                    onChange={(e) => setTier(idx, { maxDays: numOrNull(e.target.value) })}
                    className={field}
                  />
                </label>
                <label className="block space-y-0.5">
                  <span className={label}>{currency} / day</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={t.pricePerDay ?? ''}
                    onChange={(e) => setTier(idx, { pricePerDay: numOrNull(e.target.value) })}
                    className={field}
                  />
                </label>
                <button
                  type="button"
                  aria-label="Remove tier"
                  onClick={() => onTiersChange(tiers.filter((_, i) => i !== idx))}
                  className="h-8 rounded-md border border-[color:var(--bo-line)] px-2 text-[12px] text-[#9f1239] hover:border-[#fecaca]"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[12px] font-medium text-[color:var(--bo-ink)]">
            Price exceptions (specific dates / seasons)
          </span>
          <button
            type="button"
            onClick={() =>
              onExceptionsChange([
                ...exceptions,
                { dateFrom: '', dateTo: '', pricePerDay: null, label: '' },
              ])
            }
            className="text-[11.5px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            + Add exception
          </button>
        </div>
        <p className="mb-2 text-[11px] text-[color:var(--bo-mute)]">
          Days inside a range are charged this rate instead of the tier/flat rate — stays are
          priced as a per-day mix. Overlaps: the lower row wins.
        </p>
        {exceptions.length > 0 ? (
          <ul className="space-y-1.5">
            {exceptions.map((e, idx) => (
              <li key={idx} className="grid grid-cols-[1.2fr_1.2fr_1fr_1fr_auto] items-end gap-2">
                <label className="block space-y-0.5">
                  <span className={label}>From</span>
                  <input
                    type="date"
                    value={e.dateFrom}
                    onChange={(ev) => setException(idx, { dateFrom: ev.target.value })}
                    className={field}
                  />
                </label>
                <label className="block space-y-0.5">
                  <span className={label}>To (inclusive)</span>
                  <input
                    type="date"
                    value={e.dateTo}
                    onChange={(ev) => setException(idx, { dateTo: ev.target.value })}
                    className={field}
                  />
                </label>
                <label className="block space-y-0.5">
                  <span className={label}>{currency} / day</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={e.pricePerDay ?? ''}
                    onChange={(ev) => setException(idx, { pricePerDay: numOrNull(ev.target.value) })}
                    className={field}
                  />
                </label>
                <label className="block space-y-0.5">
                  <span className={label}>Label</span>
                  <input
                    value={e.label}
                    placeholder="e.g. August"
                    onChange={(ev) => setException(idx, { label: ev.target.value })}
                    className={field}
                  />
                </label>
                <button
                  type="button"
                  aria-label="Remove exception"
                  onClick={() => onExceptionsChange(exceptions.filter((_, i) => i !== idx))}
                  className="h-8 rounded-md border border-[color:var(--bo-line)] px-2 text-[12px] text-[#9f1239] hover:border-[#fecaca]"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
